<template>
    <div class="quantity-selector">
        <div class="quantity-container">
            <i class="far fa-circle-minus quantity-btn" :class="{ disabled: !canDecrement }" @click="decrement"></i>
            <span class="value">{{ currentQuantity }}</span>
            <i class="far fa-circle-plus quantity-btn" :class="{ disabled: !canIncrement }" @click="increment"></i>
        </div>
        <VButton class="primary" :disabled="!canSave" @click="save">{{ trans.general.save }}</VButton>
    </div>
</template>

<script setup lang="ts">
    import { useBillStore } from "@/data/stores/bill";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { trans } from "@/operator/localization/Trans";
    import { computed, ref } from "vue";

    import VButton from "@/components/elements/VButton.vue";

    const bill = useBillStore();
    const tableAllocation = useTableAllocationStore();

    const emit = defineEmits<{
        updated: [quantity: number];
    }>();

    const disabled = computed(() => bill.currentBill == null);
    const originalEndUsersCount = computed(() => bill.currentBill?.manualEndUsersCount);
    const hasManualEndUsersCount = computed(() => originalEndUsersCount.value != null);
    const initialQuantity = computed(() =>
        Math.max(tableAllocation.allocation?.guestNumber ?? 0, tableAllocation.allocation?.tableSeats ?? 0)
    );
    const currentQuantity = ref(originalEndUsersCount.value ?? initialQuantity.value);

    const hasChanged = computed(() => {
        if (hasManualEndUsersCount.value) {
            return currentQuantity.value !== originalEndUsersCount.value;
        }
        return true;
    });

    const canDecrement = computed(() => currentQuantity.value > 0 && !disabled.value);
    const canIncrement = computed(() => !disabled.value);
    const canSave = computed(() => hasChanged.value && !disabled.value);

    function decrement() {
        if (currentQuantity.value > 0) {
            currentQuantity.value--;
        }
    }

    function increment() {
        currentQuantity.value++;
    }

    function save() {
        emit("updated", currentQuantity.value);
    }
</script>

<style scoped lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .quantity-selector {
        display: flex;
        align-items: center;
        gap: 1em;

        .quantity-container {
            display: flex;
            align-items: center;
        }

        i.quantity-btn {
            font-size: 1.5em;
            cursor: pointer;
            color: colors.$fontColorHover;
            transition: color 0.2s;

            &:hover {
                color: colors.$fontColor;
            }

            &.disabled {
                opacity: 0.3;
                pointer-events: none;
            }

            padding: 4px;
            margin: -4px 0;
        }

        .value {
            font-size: 1.25em;
            min-width: 1.5em;
            text-align: center;
            color: #444;
        }

        .v-btn {
            font-size: 1em;
        }
    }
</style>
